import React, { useState, useEffect, useRef } from 'react';
import { useLocation, history } from 'umi';
import { Row, Col } from 'antd';
import { Rule } from 'antd/lib/form';
import { useIntl } from 'react-intl';
import { Form, Button, LocaleProvider, Typography } from 'iglooform';
import { AlertFilled } from 'iglooicon';
import { HeaderTitle } from '@/components/header-title/header-title';
import useEmailVerification from '@/components/otp-verify/useEmailVerification';
import { VerifyModal } from '@/components/otp-verify/useVerifyModal';
import { updateEmail, checkOtpCode } from '../service';
import { updateRequest } from '@/service/request';
import { emailReg } from '@/utils/regexp';
import _ from 'lodash';
import './otp.less';

const OtpVerify = () => {
  const location: any = useLocation();
  let { email, currentLang, token, from } = location.query;
  const { formatMessage } = useIntl();
  const form = Form.useForm();
  const [verifyError, setVerifyError] = useState<boolean>(false);
  const [verifyBtnLoading, setVerifyBtnLoading] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  let timer = useRef<any>(null);

  const otpSuffix = verifyError ? (
    <AlertFilled className="errorSuffixIcon" />
  ) : undefined;

  const otpErrorMessage = verifyError ? (
    <Typography level="body2" style={{ color: '#F52209' }}>
      {formatMessage({ id: 'OTP code is not valid' })}
    </Typography>
  ) : (
    ''
  );

  const {
    // emailSuffix,
    otpCodeExtraLabel,
    onEmailChange,
    // emailVerifiSuccess,
    // setEmailVerifiSuccess
  } = useEmailVerification('agency', email);

  useEffect(() => {
    localStorage.setItem(SELECTED_LANG, currentLang);
    // 如果是从app跳转过来，要set token
    if (from === 'app') {
      localStorage.setItem(TOKEN_NAME, token);
      updateRequest(currentLang);
    }
    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, []);

  const handleVerify = () =>
    _.throttle(
      async () => {
        const newEmail = form.getFieldValue('email');
        if (newEmail) {
          try {
            setVerifyBtnLoading(true);

            // check OTP code
            const checkOtpCodeRes = await checkOtpCode({
              email: newEmail.trim(),
              code: otpCode,
            });

            if (checkOtpCodeRes?.valid) {
              try {
                // update email
                const updateEmailRes = await updateEmail({
                  email: newEmail.trim(),
                });
                if (!updateEmailRes.message || !updateEmailRes.error) {
                  setVerifyModal(true);
                  // 1秒后跳转到home页
                  timer.current = setTimeout(() => {
                    setVerifyModal(false);
                    from === 'pc'
                      ? history.push('/home')
                      : (window.location.href = 'https://igniteback');
                  }, 1000);
                }
              } catch (error) {
                setVerifyBtnLoading(false);
                console.error('update email error: ', error);
              }
            } else {
              setVerifyError(true);
            }
            setVerifyBtnLoading(false);
          } catch (error) {
            setVerifyError(true);
            setVerifyBtnLoading(false);
            console.log('otp verify error: ', error);
          }
        }
      },
      1000,
      { leading: true, trailing: false },
    )();

  return (
    <LocaleProvider currentLang={currentLang}>
      <Row style={{ marginTop: '80px' }}>
        <Col span={24} style={{ maxWidth: '538px', margin: '0 auto' }}>
          <HeaderTitle
            title={formatMessage({
              id: 'Verify Your Email',
              defaultMessage: 'Verify Your Email',
            })}
          />
          <div className="otpForm">
            <Form
              className="formContainer"
              form={form}
              showSubmitButton={false}
              config={{
                type: 'Page',
                elements: [
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Email',
                      defaultMessage: 'Email',
                    }),
                    name: 'email',
                    fullRow: true,
                    required: true,
                    initialValue: email,
                    // extra: emailExtra,
                    // suffix: emailSuffix,
                    // status: emailVerifiSuccess ? undefined : 'error',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      onEmailChange(e);
                      verifyError && setVerifyError(false);
                    },
                    rules: [
                      {
                        validator: (_: Rule, value: string) => {
                          if (
                            !!value &&
                            (!emailReg.test(value) ||
                              /[\r|\n|\t|\r\n]/.test(value))
                          ) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid email format',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'OTP Code',
                      defaultMessage: 'OTP Code',
                    }),
                    name: 'otpCode',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    extraLabel: otpCodeExtraLabel,
                    suffix: otpSuffix,
                    extra: otpErrorMessage,
                    status: verifyError ? 'error' : undefined,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      setOtpCode(e?.target?.value);
                      setVerifyError(false);
                    },
                  },
                ],
              }}
              locales={{
                validateMessages: {
                  required:
                    '${label} ' +
                    formatMessage({
                      id: 'is required',
                    }),
                },
              }}
            />
            <Button
              style={{ width: '100%', marginTop: '50px' }}
              type="primary"
              onClick={handleVerify}
              disabled={!otpCode.length}
              loading={verifyBtnLoading}
            >
              {formatMessage({ id: 'Verify', defaultMessage: 'Verify' })}
            </Button>
          </div>
        </Col>
      </Row>

      <VerifyModal visible={verifyModal} />
    </LocaleProvider>
  );
};

export default OtpVerify;
